import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  Link,
  Button,
  NavbarMenu,
  NavbarMenuItem,
  Avatar,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from '@heroui/react';
import { useAuth } from '../AuthContext';

// NaachLogo.png logo for NJ Naach
export const AcmeLogo = () => (
  <img 
    src="/images/NaachLogo.png" 
    alt="NJ Naach Logo" 
    className="h-7 w-auto opacity-90 hover:opacity-100 transition-opacity uration-200"
  />
);

const PageTemplate = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { currentUser, logout, userRole, userData } = useAuth();
  const { pathname } = useLocation();

  const accentCycle = [
    '/',
    '/teams',
    '/philanthropy',
    '/board',
    '/sponsorship',
    '/history',
    '/judges',
    '/schedule',
    '/merch',
    '/board-2-0',
    '/board-1-0',
    '/login',
    '/portal',
    '/admin',
  ];
  const accentClasses = ['boardwalk-a', 'boardwalk-b', 'boardwalk-c'];
  const accentIndex = Math.max(0, accentCycle.indexOf(pathname));
  const accentClass = accentClasses[accentIndex % accentClasses.length];

  const menuItems = [
    { name: "Home", path: "/" },
    { name: "Teams", path: "/teams" },
    { name: "Philanthropy", path: "/philanthropy" },
    { name: "Board", path: "/board" },
    { name: "Sponsors", path: "/sponsorship" },
    { name: "History", path: "/history" },
  ];

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-cream-50 via-lavender-50 to-cream-50 flex flex-col ${accentClass}`}>

      {/* ───────────────────────── Beach-Themed Responsive Navbar ───────────────────────── */}
      <Navbar 
        shouldHideOnScroll 
        isBordered 
        position="sticky"
        className="backdrop-blur-md bg-white border-b border-gray-200 shadow-lg min-h-[5rem] sm:min-h-[6rem] z-50"
        onMenuOpenChange={setIsMenuOpen}
        isMenuOpen={isMenuOpen}
      >
        <NavbarContent>
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="sm:hidden p-2 text-blue-600"
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isMenuOpen ? (
                <path d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
          <NavbarBrand as={RouterLink} to="/" className="cursor-pointer">
            <AcmeLogo />
            <span className="font-text text-black ml-2 text-lg sm:text-xl">
              NJ Naach
            </span>
          </NavbarBrand>
        </NavbarContent>

        {/* Desktop Navigation */}
        <NavbarContent className="hidden sm:flex gap-6 lg:gap-8 xl:gap-10" justify="center">
          {menuItems.map((item) => (
            <NavbarItem key={item.name}>
              <Link 
                as={RouterLink} 
                to={item.path} 
                className="text-sky-500 hover:text-sky-600 transition-all duration-200 font-text font-medium text-base lg:text-lg py-2 hover:text-xl lg:hover:text-2xl hover:font-bold"
              >
                {item.name}
              </Link>
            </NavbarItem>
          ))}
        </NavbarContent>

        {/* Desktop Buttons */}
        <NavbarContent justify="end" className="hidden sm:flex">
          <NavbarItem>
            <Button
              as="a"
              href="https://www.zeffy.com/en-US/ticketing/nj-naach--2026?utm_source=ig&utm_medium=social&utm_content=link_in_bio"
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              className="bg-teal text-white px-4 lg:px-6 py-3 rounded-full hover:bg-teal-400 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 mr-2 font-button font-semibold text-xs lg:text-sm"
            >
              Tickets
            </Button>
          </NavbarItem>
          
          {currentUser ? (
            <>
              <NavbarItem>
                <Dropdown>
                  <DropdownTrigger>
                    <Avatar 
                      name={(userData?.username || userData?.teamName || 'U').charAt(0).toUpperCase()} 
                      className="bg-gradient-to-r from-beach-400 to-seafoam-400 cursor-pointer"
                      size="sm"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      classNames={{
                        base: "flex items-center justify-center",
                        name: "flex items-center justify-center !leading-none"
                      }}
                    />
                  </DropdownTrigger>
                  <DropdownMenu aria-label="User menu">
                    <DropdownItem 
                      key="dashboard"
                      as={RouterLink}
                      to="/portal"
                    >
                      Dashboard
                    </DropdownItem>
                    {userRole === 'admin' && (
                      <DropdownItem 
                        key="admin"
                        as={RouterLink}
                        to="/admin"
                      >
                        Admin Panel
                      </DropdownItem>
                    )}
                    <DropdownItem key="logout" color="danger" onClick={handleLogout}>
                      Logout
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </NavbarItem>
            </>
          ) : (
            false && (
              <NavbarItem>
                <Button
                  as={RouterLink}
                  to="/login"
                  size="sm"
                  className="bg-pink text-white px-4 lg:px-6 py-3 rounded-full hover:bg-pink-400 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 font-button font-semibold text-xs lg:text-sm"
                >
                  Log In
                </Button>
              </NavbarItem>
            )
          )}
        </NavbarContent>

        {/* Mobile Menu */}
        <NavbarMenu className="pt-8 bg-white shadow-xl fixed top-[5rem] left-0 right-0 bottom-0 z-40 overflow-y-auto">
          <div className="flex flex-col gap-4">
            {menuItems.map((item) => (
              <NavbarMenuItem key={item.name}>
                <Link
                  as={RouterLink}
                  to={item.path}
                  className="w-full text-lavender hover:text-lavender-400 transition-colors duration-200 font-text font-medium text-lg py-3 px-4 rounded-lg hover:bg-cream-50"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              </NavbarMenuItem>
            ))}
            
            {/* Mobile Buttons */}
            <div className="flex flex-col gap-3 pt-4 border-t border-sand-200">
              <Button
                as="a"
                href="https://www.zeffy.com/en-US/ticketing/nj-naach--2026?utm_source=ig&utm_medium=social&utm_content=link_in_bio"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-teal text-white py-3 rounded-lg hover:bg-teal-400 transition-all duration-200 font-button font-semibold"
                onClick={() => setIsMenuOpen(false)}
              >
                Tickets
              </Button>
              
              {currentUser ? (
                <>
                  <Link
                    as={RouterLink}
                    to="/portal"
                    className="w-full text-beach-700 hover:text-beach-500 transition-colors duration-200 font-ocean font-medium text-lg py-3 px-4 rounded-lg hover:bg-beach-50 text-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {userData?.username || userData?.teamName || 'Dashboard'}
                  </Link>
                  <Button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className="w-full bg-gradient-to-r from-coral-400 to-coral-500 text-white py-3 rounded-lg hover:from-coral-500 hover:to-coral-600 transition-all duration-200 font-button font-semibold"
                  >
                    Logout
                  </Button>
                </>
              ) : (
                false && (
                  <Button
                    as={RouterLink}
                    to="/login"
                    className="w-full bg-pink text-white py-3 rounded-lg hover:bg-pink-400 transition-all duration-200 font-button font-semibold"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Log In
                  </Button>
                )
              )}
            </div>
          </div>
        </NavbarMenu>
      </Navbar>

      {/* ───────────────────────── Main Content ───────────────────────── */}
      <main className="flex-1">
        {children}
      </main>

      {/* ───────────────────────── Beach-Themed Footer ───────────────────────── */}
      <footer className="bg-gradient-to-r from-lavender to-teal text-white py-6 sm:py-8 mt-auto">
        <div className="max-w-7xl mx-auto px-4 text-center">
          {/* Social Media Links */}
          <div className="mb-4 sm:mb-6">
            <div className="flex justify-center items-center gap-4 sm:gap-6">
              <a
                href="https://www.tiktok.com/@nj.naach"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:scale-110 transition-transform duration-300 flex items-center justify-center"
                aria-label="Follow us on TikTok"
              >
                <img 
                  src="/tiktok.webp" 
                  alt="TikTok" 
                  className="w-12 h-12 sm:w-14 sm:h-14 object-contain"
                />
              </a>
              <a
                href="https://www.youtube.com/@NJNaach"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:scale-110 transition-transform duration-300 flex items-center justify-center"
                aria-label="Subscribe on YouTube"
              >
                <img 
                  src="/youtube.webp" 
                  alt="YouTube" 
                  className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
                />
              </a>
              <a
                href="https://www.instagram.com/njnaach/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:scale-110 transition-transform duration-300 flex items-center justify-center"
                aria-label="Follow us on Instagram"
              >
                <img 
                  src="/instagram.png" 
                  alt="Instagram" 
                  className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
                />
              </a>
              <a
                href="https://linktr.ee/njnaach?fbclid=PAZXh0bgNhZW0CMTEAAaYg7q7Bn-rcz1MuT5LSq_kxEJOjKstkbTu0klepy_VbaA_e9xKo1aF_YHw_aem_XwFPCHJiDFw3K1eh2P65sQ"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:scale-110 transition-transform duration-300 flex items-center justify-center"
                aria-label="View all links"
              >
                <img 
                  src="/linktre.png" 
                  alt="Linktree" 
                  className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
                />
              </a>
            </div>
          </div>
          
          <div className="border-t border-cream-300/30 pt-4 sm:pt-6">
            <p className="text-white mb-2 font-text text-sm sm:text-base">
              © 2026 NJ Naach. All rights reserved.
            </p>
            <p className="text-white text-xs sm:text-sm font-text">
              Developed by Prithvi Sunkavelli and Shatkratu Swarnkar
            </p>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default PageTemplate;
